/**
 * Utility functions for DynamoDB partition parsing and validation
 */

/**
 * Parse partition timestamp to Unix seconds
 *
 * Formats:
 * - minute: "2006-01-02 15:04"
 * - hour: "2006-01-02 15h" (note the 'h' suffix)
 * - day: "2006-01-02"
 * - week: "2006-W01" (ISO week format)
 * - month: "2006-01" (year-month)
 * - year: "2006" (year)
 */
export function parseTimestampFromPartition(
	partition: string,
	statsType: string,
): number {
	let date: Date;
	let cleanPartition = partition;

	if (statsType === "minute") {
		// Format: "2024-01-20 15:04"
		const match = cleanPartition.match(
			/^(\d{4})-(\d{2})-(\d{2}) (\d{1,2}):(\d{2})$/,
		);
		if (!match) {
			throw new Error(`Invalid minute partition format: "${partition}"`);
		}
		const [, year, month, day, hour, minute] = match;
		date = new Date(
			`${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:00Z`,
		);
	} else if (statsType === "hour") {
		// Format: "2024-01-20 15h" -> remove 'h' suffix before parsing
		cleanPartition = partition.replace(/h$/, "");
		const match = cleanPartition.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{1,2})$/);
		if (!match) {
			throw new Error(`Invalid hour partition format: "${partition}"`);
		}
		const [, year, month, day, hour] = match;
		date = new Date(`${year}-${month}-${day}T${hour.padStart(2, "0")}:00:00Z`);
	} else if (statsType === "day") {
		// Format: "2024-01-20"
		const match = cleanPartition.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (!match) {
			throw new Error(`Invalid day partition format: "${partition}"`);
		}
		date = new Date(`${cleanPartition}T00:00:00Z`);
	} else if (statsType === "week") {
		// Format: "2024-W02" (ISO week format)
		const match = cleanPartition.match(/^(\d{4})-W(\d{2})$/);
		if (!match) {
			throw new Error(`Invalid week partition format: "${partition}"`);
		}
		const [, year, week] = match;
		const yearNum = parseInt(year);
		const weekNum = parseInt(week);
		// Convert ISO week to date (first day of week)
		const simple = new Date(`${year}-01-01T00:00:00Z`);
		const dayOfWeek = simple.getUTCDay();
		const firstMonday = new Date(simple);
		firstMonday.setUTCDate(simple.getUTCDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
		firstMonday.setUTCDate(firstMonday.getUTCDate() + (weekNum - 1) * 7);
		date = firstMonday;
	} else if (statsType === "month") {
		// Format: "2024-01"
		const match = cleanPartition.match(/^(\d{4})-(\d{2})$/);
		if (!match) {
			throw new Error(`Invalid month partition format: "${partition}"`);
		}
		const [, year, month] = match;
		date = new Date(`${year}-${month}-01T00:00:00Z`);
	} else if (statsType === "year") {
		// Format: "2024"
		const match = cleanPartition.match(/^(\d{4})$/);
		if (!match) {
			throw new Error(`Invalid year partition format: "${partition}"`);
		}
		const [, year] = match;
		date = new Date(`${year}-01-01T00:00:00Z`);
	} else {
		throw new Error(`Unknown stats type: "${statsType}"`);
	}

	if (isNaN(date.getTime())) {
		throw new Error(
			`Failed to parse partition: "${partition}" as ${statsType}`,
		);
	}

	return Math.floor(date.getTime() / 1000); // Return Unix seconds
}

/**
 * Validate time range against maximum allowed durations
 * Enforces:
 * - minute queries: max 1 day + buffer (90,000 seconds)
 * - hour queries: max 3 days + buffer (270,000 seconds)
 * - day queries: max 60 days + buffer (5,400,000 seconds)
 * - week queries: max 365 days + buffer (32,850,000 seconds)
 * - month queries: max 3 years + buffer (98,550,000 seconds)
 * - year queries: max 5 years + buffer (164,250,000 seconds)
 */
export function validateTimeRange(
	startSeconds: number,
	endSeconds: number,
	statsType: string,
): void {
	if (startSeconds > endSeconds) {
		throw new Error("Start timestamp must be <= end timestamp");
	}

	const rangeSeconds = endSeconds - startSeconds;

	let maxSeconds: number;
	switch (statsType) {
		case "minute":
			maxSeconds = 1 * 25 * 3600; // 1 day + buffer
			break;
		case "hour":
			maxSeconds = 3 * 25 * 3600; // 3 days + buffer
			break;
		case "day":
			maxSeconds = 60 * 25 * 3600; // 60 days + buffer
			break;
		case "week":
			maxSeconds = 365 * 25 * 3600; // 365 days + buffer
			break;
		case "month":
			maxSeconds = 3 * 365 * 25 * 3600; // 3 years + buffer
			break;
		case "year":
			maxSeconds = 5 * 365 * 25 * 3600; // 5 years + buffer
			break;
		default:
			throw new Error(`Invalid type: "${statsType}"`);
	}

	if (maxSeconds > 0 && rangeSeconds > maxSeconds) {
		throw new Error(
			`Time range exceeds limit for '${statsType}': ${rangeSeconds}s > ${maxSeconds}s`,
		);
	}
}

/**
 * Normalize granularity/stats type input
 * Accepts various forms and returns canonical: "minute", "hour", "day", "week", "month", or "year"
 */
export function normalizeStatsType(inputType: string): string {
	const normalized = inputType.toLowerCase().trim();

	switch (normalized) {
		case "minute":
		case "minutes":
		case "min":
			return "minute";
		case "hour":
		case "hours":
		case "hr":
			return "hour";
		case "day":
		case "daily":
		case "d":
			return "day";
		case "week":
		case "weekly":
		case "w":
			return "week";
		case "month":
		case "monthly":
		case "m":
			return "month";
		case "year":
		case "yearly":
		case "y":
			return "year";
		default:
			throw new Error(
				`Invalid granularity: "${inputType}" - must be 'minute', 'hour', 'day', 'week', 'month', or 'year'`,
			);
	}
}
